import { useEffect, useState } from "react"
import Flex from "./Flex"
import Icons from "./Icons"
import { Form, Input, Textarea, FormItem, useFormInstance } from "./form/Form"
import leazyAiCrmApi, { TranslationRequest } from "../api/leazyAiCrm.api"
import datasource from "../datasource"
import regex from "../regex"

import "./TranslationRequestModal.scss"

interface ModalCopy {
    title: string
    subTitle: string
    submitLabel: string
    submittingLabel: string
    success: string
    fail: string
    successNote: string
    email: string
    fields: {
        name: string
        email: string
        languages: string
        languagesPlaceholder: string
        deadline: string
        deadlinePlaceholder: string
        details: string
        detailsPlaceholder: string
    }
}

const copy = (datasource.translationService as any).modal as ModalCopy

const required = (msg?: string) => ({ accept: (v: string) => !!v && v.trim() !== "", msg, show: false })

export default (props: { open: boolean, onClose: () => void, mobile?: boolean }) => {

    const form = useFormInstance()
    const [loading, setLoading] = useState<boolean>(false)
    const [sent, setSent] = useState<boolean>(false)
    const [failed, setFailed] = useState<boolean>(false)

    // close on Escape, and lock body scroll while the dialog is up
    useEffect(() => {
        if (!props.open) { return }
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { props.onClose() } }
        document.addEventListener("keydown", onKey)
        const previous = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.removeEventListener("keydown", onKey)
            document.body.style.overflow = previous
        }
    }, [props.open, props.onClose])

    // start each opening from a clean slate
    useEffect(() => {
        if (props.open) {
            setSent(false)
            setFailed(false)
        }
    }, [props.open])

    if (!props.open) { return null }

    const doSubmit = () => {
        if (!form.finish || loading) { return }
        setLoading(true)
        setFailed(false)
        leazyAiCrmApi.translationRequest(form.data as TranslationRequest)
            .then(() => {
                form.clear()
                setSent(true)
            })
            .catch(() => setFailed(true))
            .finally(() => setLoading(false))
    }

    const prefix = props.mobile ? "mb-contactus" : "contactus"

    return (
        <div
            className={["rb-modal", props.mobile ? "rb-modal_mobile" : ""].join(" ")}
            onClick={props.onClose}
        >
            <div
                className="rb-modal__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="rb-modal-title"
                onClick={e => e.stopPropagation()}
            >
                <div className="rb-modal__close" onClick={props.onClose} aria-label="Close">
                    <Icons.NotificationClose width={16} height={16}/>
                </div>

                {sent
                    ? (
                        <Flex vertical gap={16} reposive={false} className="rb-modal__done">
                            <Icons.Success width={40} height={40}/>
                            <div className="rb-modal__title" id="rb-modal-title">{copy.success}</div>
                            <div className="rb-modal__subTitle">
                                {copy.successNote}{" "}
                                <a className="rb-modal__email" href={`mailto:${copy.email}`}>{copy.email}</a>
                            </div>
                            <div className="rb-modal__submit" onClick={props.onClose}>Close</div>
                        </Flex>
                    )
                    : (
                        <Flex vertical gap={20} reposive={false}>
                            <Flex vertical gap={6} reposive={false}>
                                <div className="rb-modal__title" id="rb-modal-title">{copy.title}</div>
                                <div className="rb-modal__subTitle">{copy.subTitle}</div>
                            </Flex>

                            <Form form={form} prefix={prefix}>
                                <Flex vertical gap={14} reposive={false} className="rb-modal__fields">
                                    <FormItem name="name" label={copy.fields.name} required rules={[required()]}>
                                        <Input/>
                                    </FormItem>
                                    <FormItem
                                        name="email"
                                        label={copy.fields.email}
                                        required
                                        rules={[
                                            { accept: (v: string) => !v || regex.email.test(v), msg: "Please enter the correct email address", show: true },
                                            required()
                                        ]}
                                    >
                                        <Input/>
                                    </FormItem>
                                    <FormItem name="languages" label={copy.fields.languages} required rules={[required()]}>
                                        <Input placeholder={copy.fields.languagesPlaceholder}/>
                                    </FormItem>
                                    <FormItem name="deadline" label={copy.fields.deadline}>
                                        <Input placeholder={copy.fields.deadlinePlaceholder}/>
                                    </FormItem>
                                    <FormItem name="details" label={copy.fields.details} required rules={[required()]}>
                                        <Textarea placeholder={copy.fields.detailsPlaceholder}/>
                                    </FormItem>
                                </Flex>
                            </Form>

                            {failed ? <div className="rb-modal__error">{copy.fail}</div> : null}

                            <div
                                className={["rb-modal__submit", (!form.finish || loading) ? "rb-modal__submit_disabled" : ""].join(" ")}
                                onClick={doSubmit}
                            >
                                {loading ? copy.submittingLabel : copy.submitLabel}
                            </div>
                        </Flex>
                    )
                }
            </div>
        </div>
    )
}
